import React, { useCallback, useEffect, useState } from 'react';
import { useLazyQuery, useQuery, useMutation } from '@apollo/client/react';
import {
  TaskStatus,
  TaskFragment,
  UpdateTaskInput,
  GetTaskDocument,
  UpdateTaskDocument,
  DeleteTaskDocument,
  AddTaskCommentDocument,
  UpdateTaskCommentDocument,
  DeleteTaskCommentDocument,
  AddTaskCommentMutation,
  AddTaskCommentMutationVariables,
  DeleteTaskCommentMutation,
  DeleteTaskCommentMutationVariables,
  DeleteTaskMutation,
  DeleteTaskMutationVariables,
  GetTaskQuery,
  GetTaskQueryVariables,
  UpdateTaskCommentMutation,
  UpdateTaskCommentMutationVariables,
  UpdateTaskMutation,
  UpdateTaskMutationVariables,
  GetUnitsDocument,
  GetUnitsQuery,
  GetUnitsQueryVariables,
  GetContractsDocument,
  GetContractsQuery,
  GetContractsQueryVariables,
  UndeleteTaskMutation,
  UndeleteTaskMutationVariables,
  UndeleteTaskDocument,
} from '../generated/graphql';
import { Stack, Grid, Button, Box, Alert, CircularProgress } from '@mui/material';

import { TaskBasicInfoPanel } from '../pages/task-detail/components/task-basic-info-panel';
import { ConfirmationDialog } from './confirmation-dialog';
import { useAlert } from '@dwelvio/react-alert';
import { InputComment } from './input-comment';
import { CommentList } from './comments-list';
import { DeletedItemBanner } from './deleted-item-banner';
import { useApp } from './app-context';

interface FormFields {
  title: string;
  description?: string | null;
  status: TaskStatus;
  startDate?: Date;
  dueDate?: Date;
  unitId?: string;
  contractId?: string;
}

export interface TaskDetailContentProps {
  taskId: string;
  /** Called when the task title changes, so the parent can update its heading. */
  onTitleChange?: (title: string) => void;
}

export function TaskDetailContent({ taskId, onTitleChange }: TaskDetailContentProps) {
  const app = useApp();
  const { show } = useAlert();

  const [taskToDelete, setTaskToDelete] = useState<TaskFragment | null>(null);
  const [taskToUndelete, setTaskToUndelete] = useState<TaskFragment | null>(null);

  const [fetchTask, { data, loading, error }] = useLazyQuery<GetTaskQuery, GetTaskQueryVariables>(GetTaskDocument);

  const [updateTask, { loading: updateLoading }] = useMutation<UpdateTaskMutation, UpdateTaskMutationVariables>(
    UpdateTaskDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa aktualizovať úlohu.', e.message);
      },
    },
  );

  const [deleteTask, { loading: deleteLoading }] = useMutation<DeleteTaskMutation, DeleteTaskMutationVariables>(
    DeleteTaskDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa zmazať úlohu.', e.message);
      },
    },
  );

  const [undeleteTask, { loading: undeleteLoading }] = useMutation<UndeleteTaskMutation, UndeleteTaskMutationVariables>(
    UndeleteTaskDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa obnoviť úlohu.', e.message);
      },
    },
  );

  const [addComment] = useMutation<AddTaskCommentMutation, AddTaskCommentMutationVariables>(AddTaskCommentDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa pridať komentár.', e.message);
    },
  });

  const [updateComment] = useMutation<UpdateTaskCommentMutation, UpdateTaskCommentMutationVariables>(
    UpdateTaskCommentDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa aktualizovať komentár.', e.message);
      },
    },
  );

  const [deleteComment] = useMutation<DeleteTaskCommentMutation, DeleteTaskCommentMutationVariables>(
    DeleteTaskCommentDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa zmazať komentár.', e.message);
      },
    },
  );

  const { data: propertiesData } = useQuery<GetUnitsQuery, GetUnitsQueryVariables>(GetUnitsDocument, {
    variables: { includeArchived: false },
  });
  const { data: contractsData } = useQuery<GetContractsQuery, GetContractsQueryVariables>(GetContractsDocument);

  const properties = propertiesData?.getUnits ?? [];
  const contracts = contractsData?.getContracts ?? [];

  const handleUpdate = useCallback(
    (v: FormFields) => {
      if (!taskId || !v) {
        return;
      }

      const input: UpdateTaskInput = {
        id: taskId,
        title: v.title,
        description: v.description,
        status: v.status,
        startDate: v.startDate?.toISOString(),
        dueDate: v.dueDate?.toISOString(),
        unitId: v.unitId,
        contractId: v.contractId,
      };
      updateTask({ variables: { input: { ...input, id: taskId } } });
    },
    [taskId, updateTask],
  );

  const handleDeleteTask = (task: TaskFragment) => {
    deleteTask({ variables: { taskId: task.id } });
    setTaskToDelete(null);
  };

  const handleUndeleteTask = (task: TaskFragment) => {
    undeleteTask({ variables: { taskId: task.id } });
    setTaskToUndelete(null);
  };

  const task = data?.getTask;
  const taskLocked = !!task?.deletedAt;

  useEffect(() => {
    if (taskId) {
      fetchTask({ variables: { taskId } });
    }
  }, [taskId, fetchTask]);

  useEffect(() => {
    if (task?.title) {
      onTitleChange?.(task.title);
    }
  }, [task?.title, onTitleChange]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Nepodarilo sa načítať detaily úlohy.</Alert>;
  }

  if (!task) {
    return null;
  }

  return (
    <>
      <Stack spacing={2} sx={{ marginTop: '0.5rem' }}>
        {task.deletedAt && (
          <DeletedItemBanner
            text={`Táto úloha bola zmazaná ${app.formatDate(task.deletedAt)}.`}
            undeleteLabel="Obnoviť"
            onUndelete={() => setTaskToUndelete(task)}
          />
        )}
        <Grid container rowSpacing={{ xs: 4, sm: 4 }} columnSpacing={{ xs: 4, sm: 4 }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <TaskBasicInfoPanel
              task={task}
              onChange={handleUpdate}
              updateLoading={updateLoading}
              disabled={taskLocked}
              properties={properties}
              contracts={contracts}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack spacing={2}>
              {!task.deletedAt && !task.completedAt && (
                <Button
                  variant="outlined"
                  color="success"
                  onClick={() => updateTask({ variables: { input: { id: task.id, status: 'completed' } } })}
                  fullWidth
                >
                  Označiť ako dokončenú
                </Button>
              )}
              {!task.deletedAt && task.completedAt && (
                <Button
                  variant="outlined"
                  onClick={() => updateTask({ variables: { input: { id: task.id, status: 'todo' } } })}
                  fullWidth
                >
                  Označiť ako nedokončenú
                </Button>
              )}
              {!task.deletedAt && (
                <Button variant="outlined" color="error" onClick={() => setTaskToDelete(task)} fullWidth>
                  Zmazať úlohu
                </Button>
              )}

              <InputComment
                label="Pridať komentár"
                disabled={taskLocked}
                onSubmit={(comment) => addComment({ variables: { taskId, input: { comment } } })}
                multiline
                clearOnSubmit
              />
              <Box maxHeight={{ xs: '20rem', sm: '20rem' }} overflow={{ overflowY: 'scroll' }} sx={{ marginTop: 2 }}>
                <CommentList
                  comments={task.comments}
                  onDelete={(c) => deleteComment({ variables: { taskId, commentId: c.id } })}
                  onEdit={(commentId, comment) =>
                    updateComment({ variables: { taskId, input: { id: commentId, comment } } })
                  }
                />
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Stack>

      {taskToDelete && (
        <ConfirmationDialog
          title="Zmazanie úlohy"
          loading={deleteLoading}
          cancelLabel="Nie"
          onCancel={() => setTaskToDelete(null)}
          confirmLabel="Áno"
          onConfirm={() => handleDeleteTask(taskToDelete)}
          message={`Ste si istí, že chcete odstrániť túto úlohu?`}
        />
      )}

      {taskToUndelete && (
        <ConfirmationDialog
          title="Obnovenie úlohy"
          loading={undeleteLoading}
          cancelLabel="Nie"
          onCancel={() => setTaskToUndelete(null)}
          confirmLabel="Áno"
          onConfirm={() => handleUndeleteTask(taskToUndelete)}
          message={`Ste si istí, že chcete obnoviť túto úlohu?`}
        />
      )}
    </>
  );
}
